import { z } from 'zod';

// ─── Queue name constant shared between api and worker packages ───────────────

export const TRANSPORT_LAYER_IMPORT_QUEUE_NAME = 'transport-layer-import';

// ─── Shared enums ────────────────────────────────────────────────────────────
//
// TRANSPORT_FEATURE_SCOPES and TRANSPORT_FEATURE_MODES are the single canonical
// source of truth for these enum values. All other locations (Drizzle pgEnum
// in @ooh/db, test DDL helpers, etc.) must derive from these arrays so that
// adding a new mode requires only one edit here.

export const TRANSPORT_FEATURE_SCOPES = ['public', 'roads'] as const;
export type TransportFeatureScope = (typeof TRANSPORT_FEATURE_SCOPES)[number];

export const TRANSPORT_FEATURE_MODES = [
  'autobusy',
  'tramwaje',
  'kolej-metro',
  'metro',
  'road',
] as const;
export type TransportFeatureMode = (typeof TRANSPORT_FEATURE_MODES)[number];

export const TransportFeatureScopeSchema = z.enum(TRANSPORT_FEATURE_SCOPES);
export const TransportFeatureModeSchema = z.enum(TRANSPORT_FEATURE_MODES);

// ─── WGS84 coordinate pair [lon, lat] ───────────────────────────────────────

const LonLatSchema = z.tuple([z.number(), z.number()]);

// ─── TransportLineFeature ────────────────────────────────────────────────────
//
// Represents a single imported transport line feature.
// coordinates is either a LineString ([lon, lat][]) or
// MultiLineString ([lon, lat][][]) — both are valid depending on OSM geometry.

export const TransportLineFeatureSchema = z.object({
  id: z.string(),
  scope: TransportFeatureScopeSchema,
  mode: TransportFeatureModeSchema,
  label: z.string(),
  // Accept LineString ([lon, lat][]) or MultiLineString ([lon, lat][][])
  coordinates: z.union([z.array(LonLatSchema), z.array(z.array(LonLatSchema))]),
});

export type TransportLineFeature = z.infer<typeof TransportLineFeatureSchema>;

// ─── Bbox query ──────────────────────────────────────────────────────────────

export const TransportLineBboxQuerySchema = z.object({
  /** [minLon, minLat, maxLon, maxLat] — WGS84 */
  bbox: z.tuple([z.number(), z.number(), z.number(), z.number()]),
  /** Optional scope filter; omit to return all scopes */
  scopes: z.array(TransportFeatureScopeSchema).optional(),
});

export type TransportLineBboxQuery = z.infer<typeof TransportLineBboxQuerySchema>;

// ─── Bbox response ───────────────────────────────────────────────────────────

export const TransportLineBboxResponseSchema = z.object({
  data: z.array(TransportLineFeatureSchema),
});

export type TransportLineBboxResponse = z.infer<typeof TransportLineBboxResponseSchema>;

// ─── Nearby transport item ───────────────────────────────────────────────────

export const NearbyTransportItemSchema = z.object({
  scope: TransportFeatureScopeSchema,
  mode: TransportFeatureModeSchema,
  label: z.string(),
  /** Distance from the media-unit point to the transport line, in metres */
  distanceMeters: z.number(),
});

export type NearbyTransportItem = z.infer<typeof NearbyTransportItemSchema>;

// ─── Nearby transport response ───────────────────────────────────────────────

export const NearbyTransportResponseSchema = z.object({
  data: z.array(NearbyTransportItemSchema),
});

export type NearbyTransportResponse = z.infer<typeof NearbyTransportResponseSchema>;

// ─── Transport layer import trigger ──────────────────────────────────────────

export const TransportLayerImportTriggerResponseSchema = z.object({
  jobId: z.string(),
});

export type TransportLayerImportTriggerResponse = z.infer<
  typeof TransportLayerImportTriggerResponseSchema
>;

export const TransportLayerImportStatusSchema = z.object({
  status: z.enum(['idle', 'running', 'completed', 'failed']),
  lastRunAt: z.string().datetime().nullable(),
  lastError: z.string().nullable(),
  lastJobId: z.string().nullable(),
});

export type TransportLayerImportStatus = z.infer<typeof TransportLayerImportStatusSchema>;
